import LegalPage from "@/components/LegalPage";

export default function PrivacyPolicy() {
  return (
    <LegalPage title="Privacy Policy" testId="privacy-policy-page">
      <p>True Impact Peptides</p>
      <p>Last Updated: August 2026</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">1. Introduction</h3>
      <p>Welcome to True Impact Peptides ("True Impact Peptides," "Company," "we," "our," or "us"). True Impact Peptides is committed to protecting your privacy and handling your personal information responsibly, securely, and transparently.</p>
      <p>This Privacy Policy explains how we collect, use, disclose, store, and protect your information when you visit our website, create an account, place an order, communicate with us, or otherwise interact with our services.</p>
      <p>Our goal is to be transparent about our data practices while providing a secure and professional online experience. By using our website, you acknowledge that you have read and understand this Privacy Policy.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">2. Scope of This Privacy Policy</h3>
      <p>This Privacy Policy applies to information collected through:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>The True Impact Peptides website</li>
        <li>Customer accounts</li>
        <li>Product purchases</li>
        <li>Checkout</li>
        <li>Contact forms</li>
        <li>Newsletter subscriptions</li>
        <li>Email communications</li>
        <li>Customer support interactions</li>
        <li>Certificates of Analysis requests</li>
        <li>Wholesale inquiries</li>
        <li>Affiliate applications (if offered)</li>
        <li>Any future services made available through the Website</li>
      </ul>
      <p>This Privacy Policy does not apply to information collected by third-party websites that may be linked from our Website. We encourage you to review the privacy policies of any third-party services you visit.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">3. Information We Collect</h3>
      <p>Depending on how you interact with our Website, we may collect several categories of information.</p>

      <p className="font-medium text-slate-800">A. Personal Information</p>
      <p>When you create an account, place an order, contact us, or otherwise communicate with us, we may collect information including:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Full name</li>
        <li>Email address</li>
        <li>Shipping address</li>
        <li>Billing address</li>
        <li>Telephone number</li>
        <li>Company or laboratory name (if provided)</li>
        <li>Account username</li>
        <li>Order history</li>
        <li>Customer support communications</li>
      </ul>

      <p className="font-medium text-slate-800">B. Order Information</p>
      <p>When you purchase products through our Website, we may collect information relating to your transaction, including:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Products ordered</li>
        <li>Order number</li>
        <li>Purchase date</li>
        <li>Shipping method</li>
        <li>Order status</li>
        <li>Tracking information</li>
        <li>Payment confirmation</li>
        <li>Promotional discounts used</li>
        <li>Customer communications relating to the order</li>
      </ul>
      <p>For security reasons, complete payment card information is processed by our secure third-party payment processor and is not stored by True Impact Peptides.</p>

      <p className="font-medium text-slate-800">C. Account Information</p>
      <p>If you create a customer account, we may store:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Login credentials (encrypted where applicable)</li>
        <li>Password hashes</li>
        <li>Account preferences</li>
        <li>Saved addresses</li>
        <li>Order history</li>
        <li>Account activity</li>
        <li>Login history</li>
        <li>Security settings</li>
      </ul>
      <p>Passwords are never stored in plain text.</p>

      <p className="font-medium text-slate-800">D. Communications</p>
      <p>If you contact us, we may retain:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Contact form submissions</li>
        <li>Email correspondence</li>
        <li>Customer support requests</li>
        <li>COA requests</li>
        <li>Product questions</li>
        <li>Wholesale inquiries</li>
        <li>Affiliate inquiries</li>
        <li>Survey responses</li>
        <li>Feedback</li>
      </ul>
      <p>These communications help us respond to inquiries, improve customer service, and maintain accurate business records.</p>

      <p className="font-medium text-slate-800">E. Automatically Collected Information</p>
      <p>When you visit our Website, certain technical information may be collected automatically. This may include:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>IP address</li>
        <li>Browser type</li>
        <li>Operating system</li>
        <li>Device type</li>
        <li>Screen resolution</li>
        <li>Language settings</li>
        <li>Time zone</li>
        <li>Pages visited</li>
        <li>Time spent on pages</li>
        <li>Referring website</li>
        <li>Click activity</li>
        <li>Navigation paths</li>
        <li>Date and time of access</li>
        <li>Error logs</li>
        <li>Session information</li>
      </ul>
      <p>This information helps us improve Website performance, security, usability, and customer experience.</p>

      <p className="font-medium text-slate-800">F. Cookies and Similar Technologies</p>
      <p>Our Website uses cookies and similar technologies to improve functionality and enhance your experience. Cookies may be used to:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Keep you logged into your account</li>
        <li>Remember shopping cart contents</li>
        <li>Save Website preferences</li>
        <li>Improve security</li>
        <li>Analyze Website performance</li>
        <li>Understand visitor behavior</li>
        <li>Personalize content</li>
        <li>Support marketing efforts where applicable</li>
      </ul>
      <p>You may configure your browser to reject certain cookies, although doing so may affect Website functionality. Additional information regarding cookies is provided later in this Privacy Policy.</p>

      <p className="font-medium text-slate-800">G. Analytics Information</p>
      <p>We may use analytics tools, including Google Analytics and similar technologies, to better understand how visitors interact with our Website. These tools may collect information regarding:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Website traffic</li>
        <li>Device information</li>
        <li>User behavior</li>
        <li>Session duration</li>
        <li>Geographic region (approximate)</li>
        <li>Referring sources</li>
        <li>Popular content</li>
        <li>Conversion metrics</li>
      </ul>
      <p>Analytics information is generally collected in aggregated form and helps us improve our Website and customer experience.</p>

      <p className="font-medium text-slate-800">H. Advertising Technologies</p>
      <p>We may use advertising technologies such as the Meta (Facebook) Pixel and similar tools to better understand the effectiveness of our advertising campaigns and improve our marketing efforts. These technologies may collect information about interactions with our Website to help us measure advertising performance and deliver more relevant advertising, subject to applicable laws and user preferences.</p>

      <p className="font-medium text-slate-800">I. Newsletter and Marketing Preferences</p>
      <p>If you choose to subscribe to our newsletter or marketing communications, we may collect:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Name</li>
        <li>Email address</li>
        <li>Subscription preferences</li>
        <li>Marketing engagement</li>
        <li>Email interaction statistics</li>
      </ul>
      <p>You may unsubscribe from promotional emails at any time by using the unsubscribe link included within the email or by contacting us directly.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">4. Information We Do Not Collect</h3>
      <p>True Impact Peptides does not knowingly collect:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Social Security numbers</li>
        <li>Driver's license numbers</li>
        <li>Government-issued identification (except where voluntarily provided during fraud prevention)</li>
        <li>Medical records</li>
        <li>Health records</li>
        <li>Prescription information</li>
        <li>Biometric information</li>
        <li>Protected Health Information (PHI)</li>
        <li>Financial account credentials</li>
      </ul>
      <p>We also do not intentionally collect personal information from individuals under twenty-one (21) years of age.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-4">Part 2 — How We Use Your Information</h3>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">5. How We Use Your Information</h3>
      <p>True Impact Peptides uses the information we collect only for legitimate business purposes related to operating our Website, fulfilling customer orders, maintaining security, improving our services, and complying with applicable laws. Depending on your interactions with us, we may use your information to:</p>

      <p className="font-medium text-slate-800">A. Process Orders</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Process and fulfill orders</li>
        <li>Verify payment authorization</li>
        <li>Generate order confirmations</li>
        <li>Provide tracking information</li>
        <li>Coordinate shipping and delivery</li>
        <li>Process refunds or replacements when applicable</li>
        <li>Maintain accurate transaction records</li>
      </ul>

      <p className="font-medium text-slate-800">B. Manage Customer Accounts</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Create and maintain your account</li>
        <li>Authenticate your identity</li>
        <li>Secure your account</li>
        <li>Save your preferences</li>
        <li>Display order history</li>
        <li>Improve account functionality</li>
        <li>Provide customer support</li>
      </ul>

      <p className="font-medium text-slate-800">C. Respond to Customer Inquiries</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Respond to questions</li>
        <li>Provide product information</li>
        <li>Respond to Certificate of Analysis (COA) requests</li>
        <li>Assist with order issues</li>
        <li>Provide shipping updates</li>
        <li>Respond to wholesale inquiries</li>
        <li>Improve customer service</li>
      </ul>

      <p className="font-medium text-slate-800">D. Improve Our Website</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Improve navigation</li>
        <li>Enhance Website performance</li>
        <li>Optimize page loading speed</li>
        <li>Improve product organization</li>
        <li>Fix software bugs</li>
        <li>Improve mobile usability</li>
        <li>Develop new features</li>
        <li>Enhance the overall customer experience</li>
      </ul>

      <p className="font-medium text-slate-800">E. Security and Fraud Prevention</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Detect fraudulent transactions</li>
        <li>Prevent unauthorized account access</li>
        <li>Verify customer identity</li>
        <li>Investigate suspicious activity</li>
        <li>Protect Website infrastructure</li>
        <li>Prevent abuse of promotions</li>
        <li>Detect bots and automated attacks</li>
        <li>Comply with payment processor security requirements</li>
      </ul>

      <p className="font-medium text-slate-800">F. Marketing Communications</p>
      <p>With your consent where required by law, we may use your information to send:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Company announcements</li>
        <li>Product updates</li>
        <li>Educational content</li>
        <li>Research-related news</li>
        <li>Promotional offers</li>
        <li>Newsletter communications</li>
        <li>Website updates</li>
      </ul>
      <p>You may opt out of promotional communications at any time using the unsubscribe link included in our emails or by contacting us directly. Operational emails (such as order confirmations, shipping updates, and account notifications) may still be sent as necessary to fulfill our services.</p>

      <p className="font-medium text-slate-800">G. Legal Compliance</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Comply with applicable laws</li>
        <li>Respond to lawful governmental requests</li>
        <li>Enforce our Terms &amp; Conditions</li>
        <li>Protect our legal rights</li>
        <li>Resolve disputes</li>
        <li>Prevent fraud</li>
        <li>Meet tax and accounting obligations</li>
      </ul>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">6. What We Do NOT Do With Your Information</h3>
      <p>True Impact Peptides values customer privacy. Accordingly:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>We do not sell your personal information.</li>
        <li>We do not rent your personal information to third parties.</li>
        <li>We do not trade customer lists.</li>
        <li>We do not disclose your payment card information to advertisers.</li>
        <li>We do not share customer information for unrelated marketing purposes.</li>
        <li>We do not intentionally collect unnecessary personal information.</li>
        <li>We do not knowingly collect information from individuals under twenty-one (21) years of age.</li>
        <li>We do not use your personal information in ways inconsistent with this Privacy Policy.</li>
      </ul>
      <p>Our business is built on providing Research Use Only products — not on selling customer data.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">7. When We May Share Information</h3>
      <p>Although we do not sell your personal information, we may share limited information when necessary to operate our business. Examples include:</p>
      <p><strong>Payment Processing</strong> — To securely process payments through our third-party payment processor.</p>
      <p><strong>Shipping Providers</strong> — To deliver your orders through shipping carriers.</p>
      <p><strong>Website Service Providers</strong> — To support Website hosting, maintenance, analytics, email delivery, cloud infrastructure, security monitoring, and similar operational services.</p>
      <p><strong>Professional Advisors</strong> — With accountants, attorneys, auditors, insurers, or other professional advisors when reasonably necessary.</p>
      <p><strong>Legal Requirements</strong> — If required by law, subpoena, court order, governmental investigation, or other legal process.</p>
      <p><strong>Business Transactions</strong> — If True Impact Peptides is involved in a merger, acquisition, asset sale, corporate restructuring, or bankruptcy, customer information may be transferred as part of that transaction, subject to applicable law.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">8. Third-Party Services</h3>
      <p>Our Website may utilize trusted third-party providers to support our operations. Examples may include providers that assist with:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Payment processing</li>
        <li>Shipping</li>
        <li>Website hosting</li>
        <li>Email delivery</li>
        <li>Analytics</li>
        <li>Security monitoring</li>
        <li>Fraud prevention</li>
        <li>Customer communications</li>
        <li>Marketing tools</li>
      </ul>
      <p>Each third-party provider maintains its own privacy practices. True Impact Peptides encourages customers to review the privacy policies of those providers where appropriate.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">9. Marketing Preferences</h3>
      <p>Customers may unsubscribe from promotional communications at any time. Opting out of marketing communications does not prevent us from sending necessary transactional communications such as:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Order confirmations</li>
        <li>Shipping notifications</li>
        <li>Account security alerts</li>
        <li>Customer support responses</li>
        <li>Policy updates</li>
        <li>Important legal notices</li>
      </ul>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-4">Part 3 — Cookies, Security, Your Rights &amp; Miscellaneous</h3>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">10. Cookies and Similar Technologies</h3>
      <p>True Impact Peptides uses cookies and similar technologies to improve Website functionality, enhance security, remember user preferences, analyze Website traffic, and improve the customer experience. Cookies may be used for purposes including:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Maintaining secure login sessions</li>
        <li>Remembering shopping cart contents</li>
        <li>Saving Website preferences</li>
        <li>Preventing fraudulent activity</li>
        <li>Measuring Website performance</li>
        <li>Improving navigation</li>
        <li>Understanding visitor behavior</li>
        <li>Supporting analytics</li>
        <li>Supporting marketing campaigns where applicable</li>
      </ul>
      <p>Most web browsers allow users to manage or disable cookies through browser settings. Please note that disabling certain cookies may affect the functionality of portions of the Website, including customer accounts, shopping carts, and checkout.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">11. Analytics and Advertising Technologies</h3>
      <p>We may use trusted analytics and advertising technologies, including but not limited to Google Analytics, Meta Pixel, and similar tools, to better understand how visitors use our Website. These technologies may collect information such as:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Pages visited</li>
        <li>Time spent on pages</li>
        <li>Device type</li>
        <li>Browser type</li>
        <li>General geographic region</li>
        <li>Referring websites</li>
        <li>Click activity</li>
        <li>Conversion metrics</li>
        <li>Advertising interactions</li>
      </ul>
      <p>This information helps us improve Website performance, enhance user experience, measure advertising effectiveness, optimize Website content, and improve customer support. These providers may use cookies or similar technologies in accordance with their own privacy policies.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">12. Data Security</h3>
      <p>True Impact Peptides takes the security of customer information seriously. We implement commercially reasonable administrative, technical, and organizational safeguards designed to protect personal information against unauthorized access, disclosure, alteration, or destruction. Security measures may include:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Encrypted data transmission (HTTPS/TLS)</li>
        <li>Password hashing</li>
        <li>Access controls</li>
        <li>Secure cloud infrastructure</li>
        <li>Firewall protections</li>
        <li>Authentication controls</li>
        <li>Routine software updates</li>
        <li>Monitoring for suspicious activity</li>
        <li>Fraud detection tools</li>
        <li>Secure payment processing through trusted third-party providers</li>
      </ul>
      <p>While we strive to protect customer information, no method of electronic transmission or storage can be guaranteed to be completely secure. Accordingly, we cannot guarantee absolute security.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">13. Data Retention</h3>
      <p>We retain personal information only for as long as reasonably necessary to:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Fulfill customer orders</li>
        <li>Maintain customer accounts</li>
        <li>Provide customer support</li>
        <li>Comply with legal obligations</li>
        <li>Resolve disputes</li>
        <li>Enforce our agreements</li>
        <li>Maintain business records</li>
        <li>Prevent fraud</li>
      </ul>
      <p>When personal information is no longer required for these purposes, we will securely delete, anonymize, or otherwise dispose of the information in accordance with applicable laws and our internal data retention practices.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">14. Your Privacy Rights</h3>
      <p>Depending on your jurisdiction, you may have certain rights regarding your personal information. Subject to applicable law, these rights may include:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Requesting access to your personal information</li>
        <li>Requesting correction of inaccurate information</li>
        <li>Requesting deletion of personal information</li>
        <li>Requesting a copy of information you have provided</li>
        <li>Requesting restrictions on certain processing activities</li>
        <li>Opting out of promotional communications</li>
        <li>Requesting information regarding certain data disclosures</li>
      </ul>
      <p>To exercise applicable rights, please contact us at info@trueimpactpeptides.com. We may request information necessary to verify your identity before responding to certain requests.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">15. California Privacy Rights</h3>
      <p>If you are a California resident, you may have additional privacy rights under applicable California law, including the California Consumer Privacy Act (CCPA), as amended by the California Privacy Rights Act (CPRA). Subject to applicable law, California residents may have rights including:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>The right to know what categories of personal information we collect.</li>
        <li>The right to know how personal information is used.</li>
        <li>The right to request access to certain personal information.</li>
        <li>The right to request deletion of eligible personal information.</li>
        <li>The right to request correction of inaccurate personal information.</li>
        <li>The right to opt out of certain data sharing where applicable.</li>
        <li>The right not to receive discriminatory treatment for exercising privacy rights.</li>
      </ul>
      <p>Requests may be submitted by contacting info@trueimpactpeptides.com. We will respond in accordance with applicable California law.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">16. Do Not Track Signals</h3>
      <p>Some web browsers offer a "Do Not Track" ("DNT") feature. Because there is currently no universally accepted standard governing DNT signals, our Website does not currently respond differently to browser-based Do Not Track requests. Should an industry standard become widely adopted in the future, we may update our practices accordingly.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">17. Third-Party Websites</h3>
      <p>Our Website may contain links to third-party websites or services. True Impact Peptides is not responsible for the privacy practices, content, security, or policies of third-party websites. Accessing third-party websites is at your own risk. We encourage visitors to review the privacy policies of any third-party websites they choose to visit.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">18. Children's Privacy</h3>
      <p>Our Website is intended solely for individuals who are twenty-one (21) years of age or older. We do not knowingly collect personal information from individuals under twenty-one (21) years of age. If we become aware that personal information has been collected from an individual under the age of twenty-one (21), we will take reasonable steps to delete such information. If you believe an individual under twenty-one has submitted information to us, please contact us immediately at info@trueimpactpeptides.com.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">19. International Visitors</h3>
      <p>True Impact Peptides currently conducts business within the United States. If you access our Website from outside the United States, you acknowledge that your information may be transferred to and processed in the United States, where privacy laws may differ from those of your jurisdiction.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">20. Changes to This Privacy Policy</h3>
      <p>We reserve the right to modify this Privacy Policy at any time. When material changes are made, we will update the "Last Updated" date appearing at the top of this Privacy Policy. Continued use of the Website following publication of an updated Privacy Policy constitutes acceptance of the revised Privacy Policy.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">21. Contact Us</h3>
      <p>If you have questions regarding this Privacy Policy or our privacy practices, please contact us at:</p>
      <p>True Impact Peptides<br />Email: info@trueimpactpeptides.com</p>

      <p className="pt-4 italic text-slate-400">End of Privacy Policy</p>
    </LegalPage>
  );
}
